<template>
  <div class="w-full max-w-xl mx-auto space-y-3 sm:space-y-4 pb-4 sm:pb-8">
    <!-- Header Progress Stepper Indicator -->
    <!-- <div class="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <v-btn icon small class="hover:bg-gray-100" @click="goBack">
          <v-icon color="grey darken-2">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h2 class="text-sm sm:text-base font-bold text-gray-900 mb-0">Visit Notes & Outcome</h2>
          <p class="text-xs text-gray-500 mb-0">Step {{ currentStepIndex + 1 }} of {{ totalStepsCount }} • Meeting Details</p>
        </div>
      </div>

      <v-btn v-if="canSkip" text small color="indigo" class="rounded-lg text-xs font-bold capitalize" @click="skipStep">
        Skip
        <v-icon right x-small>mdi-chevron-double-right</v-icon>
      </v-btn>
    </div> -->

    <!-- Task Customer Badge -->
    <div v-if="taskData"
      class="bg-indigo-50/60 rounded-xl p-3 border border-indigo-100 flex items-center justify-between text-xs">
      <div>
        <span class="text-gray-500 block">Customer</span>
        <span class="font-bold text-indigo-950 text-sm">{{ customerName }}</span>
      </div>
      <span class="bg-white text-indigo-700 font-extrabold px-2.5 py-1 rounded-lg border border-indigo-200">
        {{ caseNumber }}
      </span>
    </div>

    <!-- Form Details Card -->
    <div class="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 space-y-3">
      <!-- Outcome Category Tabs (if available from orgExtendUseCase) -->
      <div v-if="outcomeCategories.length > 0">
        <label class="block text-xs font-bold text-gray-700 mb-1">Outcome Category</label>
        <div class="flex flex-wrap gap-1.5 mb-1">
          <button v-for="cat in outcomeCategories" :key="cat.value" type="button" :class="[
            'px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors',
            selectedCategory === cat.value
              ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
              : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
          ]" @click="selectCategory(cat)">
            {{ cat.label }}
          </button>
        </div>
      </div>

      <!-- Outcome Chips Only -->
      <div>
        <label class="block text-xs font-bold text-gray-700 mb-1.5">Meeting Outcome *</label>
        <div v-if="currentOutcomeOptions.length > 0" class="flex flex-wrap gap-1.5">
          <button v-for="opt in currentOutcomeOptions" :key="opt.value" type="button" :class="[
            'px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer select-none active:scale-95',
            form.outcome === opt.value
              ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
              : 'bg-indigo-50/60 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
          ]" @click="form.outcome = opt.value">
            {{ opt.text }}
          </button>
        </div>
      </div>

      <!-- Fund Collected (If Paid / Partial Paid or has amount field) -->
      <div v-if="showAmountField">
        <label class="block text-xs font-bold text-gray-700 mb-1">Fund Collected Amount (₹)</label>
        <v-text-field v-model.number="form.fundCollected" type="number" prefix="₹" placeholder="0.00" outlined dense
          hide-details class="rounded-xl text-xs"></v-text-field>
      </div>

      <!-- Meeting Outcome Date (for PTP / follow-up) -->
      <div v-if="showDateField">
        <label class="block text-xs font-bold text-gray-700 mb-1">Promised / Follow-up Date</label>
        <v-text-field v-model="form.outcomeDate" type="date" outlined dense hide-details
          class="rounded-xl text-xs"></v-text-field>
      </div>

      <!-- Meeting Notes Textarea -->
      <div>
        <label class="block text-xs font-bold text-gray-700 mb-1">Meeting Notes *</label>
        <v-textarea v-model="form.notes" rows="3"
          placeholder="Enter conversation highlights, customer response, next follow-up details..." outlined dense
          hide-details class="rounded-xl text-xs"></v-textarea>
      </div>

      <!-- Attachments Screen / Section -->
      <div class="border-t border-gray-100 pt-3">
        <div class="flex items-center justify-between mb-1.5">
          <label class="block text-xs font-bold text-gray-700 mb-0">Attachments & Documents</label>
          <span class="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
            {{ uploadedAttachments.length + pendingFiles.length }} file(s)
          </span>
        </div>
        <div>
          <p class="text-[11px] text-gray-500 mb-0">Upload visit proofs, payment receipts, or customer signatures</p>
        </div>

        <!-- Allowed proof type dropdown if configured -->
        <div v-if="allowedProofTypes.length > 0" class="mb-2">
          <label class="block text-[11px] font-semibold text-gray-600 mb-1">Select Document Proof Type</label>
          <v-select v-model="selectedProofType" :items="allowedProofTypes" outlined dense hide-details
            placeholder="Choose proof type (e.g. Receipt, Customer Letter)" class="rounded-xl text-xs"></v-select>
        </div>

        <!-- Hidden Native File Input -->
        <input ref="fileInput" type="file" class="hidden" accept="image/*,application/pdf" multiple
          @change="onFilesSelected" />

        <!-- Upload Trigger Box -->
        <div
          class="border-2 border-dashed border-gray-200 hover:border-indigo-400 bg-gray-50/60 hover:bg-indigo-50/20 rounded-xl p-3 text-center cursor-pointer transition-all"
          @click="triggerFilePicker">
          <v-icon color="indigo lighten-1" size="24">mdi-cloud-upload-outline</v-icon>
          <p class="text-xs font-bold text-gray-700 mt-1 mb-0.5">Click to choose or capture files</p>
          <p class="text-[10px] text-gray-400 mb-0">PNG, JPG, PDF up to 8MB</p>
        </div>

        <!-- Pending Selected Files List -->
        <div v-if="pendingFiles.length > 0" class="mt-2 space-y-1">
          <p class="text-[11px] font-bold text-gray-600 mb-1">Files ready to upload:</p>
          <div v-for="(file, idx) in pendingFiles" :key="'pending-' + idx"
            class="flex items-center justify-between p-2 rounded-xl bg-gray-50 border border-gray-200 text-xs">
            <div class="flex items-center gap-2 truncate pr-2">
              <v-icon small color="indigo">mdi-file-outline</v-icon>
              <span class="font-medium text-gray-800 truncate">{{ file.name }}</span>
              <span class="text-[10px] text-gray-400">({{ formatFileSize(file.size) }})</span>
            </div>
            <v-btn icon x-small color="red" @click="removePendingFile(idx)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Previously Uploaded Attachments -->
        <div v-if="uploadedAttachments.length > 0" class="mt-2 space-y-1">
          <p class="text-[11px] font-bold text-emerald-600 mb-1 flex items-center gap-1">
            <v-icon x-small color="emerald">mdi-check-circle</v-icon>
            Uploaded Attachments:
          </p>
          <div v-for="(att, idx) in uploadedAttachments" :key="'uploaded-' + idx"
            class="flex items-center justify-between p-2 rounded-xl bg-emerald-50/60 border border-emerald-200 text-xs">
            <div class="flex items-center gap-2 truncate pr-2">
              <v-icon small color="emerald">mdi-attachment</v-icon>
              <a v-if="att.url" :href="att.url" target="_blank"
                class="font-medium text-emerald-900 truncate hover:underline">
                {{ att.fileName || att.name || 'Attachment ' + (idx + 1) }}
              </a>
              <span v-else class="font-medium text-emerald-900 truncate">
                {{ att.fileName || att.name || 'Attachment ' + (idx + 1) }}
              </span>
            </div>
            <v-btn icon x-small color="gray" @click="removeUploadedAttachment(idx)">
              <v-icon small color="grey darken-1">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Action Button Footer -->
      <div class="pt-2">
        <v-btn depressed block :color="$vuetify.theme.currentTheme.employeePrimary"
          class="h-10 rounded-xl font-extrabold text-xs sm:text-sm white--text shadow-xs" :loading="submitting"
          @click="submitMeetingNotes">
          <span>Save & Proceed</span>
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getTaskById,
  getCollectionCaseById,
  updateTaskStatus,
  updateCaseStatus,
  getTaskSettings,
  getOrgConfig,
  getExtendUseCaseById,
  uploadMeetingAttachment
} from '@/app/services/dashboard'
import {
  sendStepLocation,
  computeTaskSteps,
  getNextStepRoute
} from '@/app/utils/taskFlow'

export default {
  name: 'AppTaskMeeting',
  data() {
    return {
      taskId: this.$route.params.taskId,
      isCollection: this.$route.query.isCollection === 'true' || this.$route.query.isCollection === true,
      taskData: null,
      taskSettings: null,
      orgConfig: null,
      orgExtendUseCase: null,
      submitting: false,
      stepsList: [],
      selectedCategory: '',
      selectedProofType: '',
      pendingFiles: [],
      uploadedAttachments: [],
      form: {
        outcome: 'PAID',
        fundCollected: 0,
        outcomeDate: '',
        notes: ''
      },
      defaultOutcomes: [
        { text: 'Full Payment Collected', value: 'PAID' },
        { text: 'Partial Payment Collected', value: 'PARTIAL_PAID' },
        { text: 'Promise to Pay (PTP)', value: 'PTP' },
        { text: 'Customer Dispute / Not Available', value: 'DISPUTE' },
        { text: 'Refused to Pay (RTP)', value: 'RTP' }
      ]
    }
  },
  computed: {
    customerName() {
      return (
        this.taskData?.customer?.customerFullName ||
        this.taskData?.customer?.name ||
        this.taskData?.taskTitle ||
        'Customer'
      )
    },
    caseNumber() {
      return this.taskData?.caseNo ? `#${this.taskData.caseNo}` : 'Task'
    },
    currentStepIndex() {
      const idx = this.stepsList.findIndex((s) => s.routeName === 'AppTaskMeeting')
      return idx >= 0 ? idx : 1
    },
    totalStepsCount() {
      return this.stepsList.length || 3
    },
    canSkip() {
      const current = this.stepsList.find((s) => s.routeName === 'AppTaskMeeting')
      return !!current?.canSkip
    },
    allowedProofTypes() {
      const types = this.taskSettings?.allowedProofTypes || []
      return types.map((t) => ({ text: t, value: t }))
    },
    outcomeCategories() {
      const subCase = this.orgExtendUseCase
      if (subCase && Array.isArray(subCase.userDefinedCaseStatus)) {
        return subCase.userDefinedCaseStatus.map((s) => ({
          label: s.categoryName,
          value: s._id,
          options: s.optionsList || (s.options ? s.options.map((o) => ({ code: o })) : [])
        }))
      }
      return []
    },
    currentOutcomeOptions() {
      if (this.outcomeCategories.length > 0) {
        const cat = this.outcomeCategories.find((c) => c.value === this.selectedCategory) || this.outcomeCategories[0]
        if (cat && cat.options && cat.options.length > 0) {
          return cat.options.map((opt) => ({
            text: opt.code || opt.label || opt,
            value: opt.code || opt.value || opt
          }))
        }
      }
      return this.defaultOutcomes
    },
    showAmountField() {
      const o = (this.form.outcome || '').toUpperCase()
      return o.includes('PAID') || o.includes('COLLECT') || o.includes('FUND') || o === 'PAID' || o === 'PARTIAL_PAID'
    },
    showDateField() {
      const o = (this.form.outcome || '').toUpperCase()
      return o.includes('PTP') || o.includes('PROMISE') || o.includes('FOLLOW') || o.includes('SCHEDULE')
    }
  },
  mounted() {
    this.initStep()
  },
  methods: {
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    triggerFilePicker() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files || [])
      const maxSize = 8 * 1024 * 1024
      const valid = []
      files.forEach((file) => {
        if (file.size > maxSize) {
          this.$toast?.error?.(`File ${file.name} exceeds 8MB limit`)
        } else {
          valid.push(file)
        }
      })
      this.pendingFiles.push(...valid)
      e.target.value = ''
    },
    removePendingFile(index) {
      this.pendingFiles.splice(index, 1)
    },
    removeUploadedAttachment(index) {
      this.uploadedAttachments.splice(index, 1)
    },
    selectCategory(cat) {
      this.selectedCategory = cat.value
      localStorage.setItem('osc', cat.value)
      if (cat.options && cat.options.length > 0) {
        const first = cat.options[0]
        this.form.outcome = first.code || first.value || first
      }
    },
    async initStep() {
      try {
        const [taskRes, settingsRes, orgRes] = await Promise.allSettled([
          getTaskById({ taskId: this.taskId, isCollection: this.isCollection })
            .catch(() => (this.isCollection ? getCollectionCaseById({ caseId: this.taskId }) : null)),
          getTaskSettings(),
          getOrgConfig()
        ])

        if (taskRes.status === 'fulfilled' && taskRes.value) {
          const resData =
            taskRes.value.data?.case ||
            taskRes.value.data?.task ||
            taskRes.value.data?.data?.case ||
            taskRes.value.data?.data?.task ||
            taskRes.value.data?.data ||
            taskRes.value.data
          this.taskData = resData

          // Extract existing attachments
          const existingAtts = this.taskData?.meetingDetails?.attachments || []
          if (Array.isArray(existingAtts)) {
            this.uploadedAttachments = [...existingAtts]
          }

          if (this.taskData?.meetingDetails) {
            this.form.outcome = this.taskData.meetingDetails.meetingOutCome || this.taskData.userDefinedCaseStatus || 'PAID'
            this.form.fundCollected = this.taskData.meetingDetails.fundCollected || 0
            this.form.notes = this.taskData.meetingDetails.meetingNotes || ''
            this.form.outcomeDate = this.taskData.meetingDetails.collectionStatusDate || ''
          }

          // Fetch subCase extend config if collection task has subCase
          const subCaseId = this.taskData?.case?.subCase || this.taskData?.subCase
          if (subCaseId) {
            try {
              const extRes = await getExtendUseCaseById(subCaseId)
              this.orgExtendUseCase = extRes.data?.data || extRes.data
              if (this.outcomeCategories.length > 0) {
                const cachedCat = localStorage.getItem('osc')
                const matched = this.outcomeCategories.find((c) => c.value === cachedCat)
                this.selectedCategory = matched ? matched.value : this.outcomeCategories[0].value
              }
            } catch (extErr) {
              console.warn('Could not load orgExtendUseCase:', extErr)
            }
          }
        }

        if (settingsRes.status === 'fulfilled') {
          this.taskSettings = settingsRes.value.data?.data || settingsRes.value.data
        }

        if (orgRes.status === 'fulfilled') {
          this.orgConfig = orgRes.value.data?.data || orgRes.value.data
        }

        this.stepsList = computeTaskSteps(this.taskSettings, this.orgConfig)
      } catch (err) {
        console.error('Error loading meeting step:', err)
      }
    },
    async submitMeetingNotes() {
      if (!this.form.notes) {
        this.$toast?.error?.('Please enter visit meeting notes')
        return
      }

      try {
        this.submitting = true

        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.taskId

        // Upload any pending files
        const newUploaded = []
        if (this.pendingFiles.length > 0) {
          try {
            const formData = new FormData()
            formData.append('taskId', validTaskId)
            if (this.isCollection) {
              formData.append('caseType', 'COLLECTION')
            }
            if (this.selectedProofType) {
              formData.append('proofType', this.selectedProofType)
            }
            this.pendingFiles.forEach((file) => {
              formData.append('attachments', file)
            })
            const upRes = await uploadMeetingAttachment(formData)
            const uploadedList = upRes.data?.uploads || upRes.data?.attachments || upRes.data?.data || []
            if (Array.isArray(uploadedList)) {
              uploadedList.forEach((item) => {
                newUploaded.push({
                  fileName: item.originalname || item.fileName || 'Attachment',
                  url: item.doc_physical_path || item.url || ''
                })
              })
            }
          } catch (upErr) {
            console.warn('Error uploading attachments:', upErr)
          }
        }

        // Extract existing meetingDetails if present and format properly
        let existingMeeting = this.taskData?.meetingDetails
        if (typeof existingMeeting === 'string') {
          try {
            existingMeeting = JSON.parse(existingMeeting)
          } catch (e) {
            existingMeeting = { _id: existingMeeting }
          }
        }
        if (!existingMeeting || typeof existingMeeting !== 'object') {
          existingMeeting = {}
        }

        const combinedAttachments = [...this.uploadedAttachments, ...newUploaded]

        const payload = {
          meetingDetails: {
            ...existingMeeting,
            meetingNotes: this.form.notes,
            meetingOutCome: this.form.outcome,
            fundCollected: Number(this.form.fundCollected) || 0,
            collectionStatusDate: this.form.outcomeDate || undefined,
            attachments: combinedAttachments
          },
          userDefinedCaseStatus: this.form.outcome
        }

        if (this.isCollection && this.taskData?.case?._id) {
          await updateCaseStatus({
            caseId: this.taskData.case._id,
            userDefinedCaseStatus: this.form.outcome,
            caseStatusCategory: this.selectedCategory || undefined
          }).catch((e) => console.warn(e))
        }

        await updateTaskStatus({
          taskId: validTaskId,
          isCollection: this.isCollection,
          ...payload
        })

        // Persist outcome & outcome category in localStorage for downstream questionnaire dependency and sharing
        localStorage.setItem(`lmo-${this.taskId}`, this.form.outcome)
        localStorage.setItem(`lastMeetingOutcome-${this.taskId}`, this.form.outcome)
        localStorage.setItem(`lmo-${validTaskId}`, this.form.outcome)
        localStorage.setItem(`lastMeetingOutcome-${validTaskId}`, this.form.outcome)
        localStorage.setItem(`lmn-${this.taskId}`, this.form.notes || '')
        localStorage.setItem(`lastMeetingNotes-${this.taskId}`, this.form.notes || '')
        localStorage.setItem(`lmn-${validTaskId}`, this.form.notes || '')
        localStorage.setItem(`lastMeetingNotes-${validTaskId}`, this.form.notes || '')
        if (this.selectedCategory) {
          localStorage.setItem('osc', this.selectedCategory)
          localStorage.setItem('lastOutcomeCategory', this.selectedCategory)
        }

        this.$toast?.success?.('Meeting notes saved successfully!')

        // Send telemetry on submit
        await sendStepLocation('AppTaskMeeting', this.taskData)

        this.proceedToNext()
      } catch (err) {
        console.error('Error saving meeting notes:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to save meeting notes')
      } finally {
        this.submitting = false
      }
    },
    skipStep() {
      this.proceedToNext()
    },
    proceedToNext() {
      const nextRouteName = getNextStepRoute('AppTaskMeeting', this.stepsList, this.taskData)
      this.$router.push({
        name: nextRouteName,
        params: { taskId: this.taskId },
        query: { isCollection: this.isCollection }
      })
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>
